/* eslint-disable filenames/match-exported -- Needs to alias the default export */
import { Country } from '@/constructs/Country';
import { Language } from '@/constructs/Language';
import {
  regionCountriesMap,
  type ConfiguredLocaleString,
  type ILocale,
  type SiteID
} from '@/constructs/LocaleSchema';
import { Region } from '@/constructs/Region';
import type {
  LocalizationStringMap,
  LocalizationStringPath,
  Resource
} from '@/lang';
import ResourceStrings from '@/lang/ResourceStrings';
import { Nullable, PartialRecord, Primitive } from '@/type-utils';
import MemoryCache, { GetMethod } from '@/utils/MemoryCache';
import StaleWhileRevalidate from '@/utils/StaleWhileRevalidate';
import {
  LikelyMisconfigurationError,
  NotImplementedError
} from '@/utils/errors';
import { InvalidArgumentError } from '@/utils/errors/InvalidArgumentError';
import { isNullOrEmpty } from '@/utils/null-utils';
import IntlMessageFormat from 'intl-messageformat';
import type { NextRouter } from 'next/router';
import type { JSX, ReactElement } from 'react';
import type { Currency } from '../../models/Money';
import { getHook } from '../../utils/react-utils/hook-utils';
import CurrentRequestService from '../CurrentRequestService';
import { EnvironmentService } from '../EnvironmentService';
import { RenderContextService } from '../RenderContextService';

/**
 * Provides data relating to internationalization and localization within the
 * context of the current locale (i.e. the current parameters that define the
 * user's region, country, and/or language).
 */
class I18NService {
  private _resourceStringCache: Nullable<MemoryCache<ResourceStrings>>;
  private _currency: Currency = 'USD' as Currency;

  /** Effectively a cache for ILocale objects. */
  private _ConfiguredLocaleStringRecord: PartialRecord<
    ConfiguredLocaleString | Language | Country,
    ILocale
  > = {};

  /**
   * Sets up the resource string cache when it is requested.
   * This keeps it from running too early.
   * @returns The memory cache associated with the service.
   */
  private get resourceStringCache(): MemoryCache<ResourceStrings> {
    this._resourceStringCache ??= new MemoryCache<ResourceStrings>();
    return this._resourceStringCache;
  }

  /**
   * The site currency hard coded to USD.
   * @returns The currency that is currently used on the site.
   */
  public get currency(): Currency {
    return this._currency;
  }

  /**
   * Gets the current locale (language and country) as an object.
   *
   * @returns The locale in use for the current request.
   *
   * `toString()` can be called on this value to get a string representation.
   */
  public get currentLocale(): ILocale {
    // If we're in an isolated environment, such as Storybook, do not attempt to
    // obtain the locale from any of the normal sources. Instead, use `en-US` as
    // the default.
    if ((typeof __isolatedContext__ !== "undefined")) {
      return this.getLocaleFromString('en-US' as ConfiguredLocaleString);
    }

    // If we're on the server...
    if ((typeof window === "undefined")) {
      // Try to get the locale from the request (if in a request).
      if (CurrentRequestService.inRequest) {
        const [requestObject] = CurrentRequestService.tryGet();

        if (requestObject) {
          const langLocale = requestObject.locale
            ? requestObject.locale
            : requestObject.queryParams.get('lang');

          if (!isNullOrEmpty(langLocale)) {
            return this.getLocaleFromString(
              langLocale as ConfiguredLocaleString
            );
          }
        }
      }

      // This is to make sure we are on the app router.
      if (RenderContextService.isContextSupported) {
        try {
          const { request } = RenderContextService.context;
          const langLocale = request.locale;
          return this.getLocaleFromString(langLocale as ConfiguredLocaleString);
        } catch (e) {
          // When in the pages router, the cache shouldn't exist at all.
          // In the app router it should always fail when used in a client component
          // This is only useful within a server component.
          if (!(e instanceof NotImplementedError)) {
            // If it throws the NotImplementedError we just move on
            // Since it is expected when this is checked outside a server component,
            // but any other error should be rethrown.
            throw e;
          }
        }
      }
    }

    // Attempt to get the locale from `useRouter` - this will not work in some
    // environments.
    try {
      const Router = getHook<NextRouter>('Router');
      return this.getLocaleFromString(Router.locale as ConfiguredLocaleString);
    } catch (e) {
      // If `useRouter` failed, then we're unable to determine the locale.
      throw new Error(
        `Could not determine the locale for the current environment.`,
        { cause: e as Error }
      );
    }
  }

  /**
   * Gets the current {@link Region} based on the current {@see Country}.
   * @returns The current {@link Region}.
   * @example
   * I18NService.currentRegion; // 'NA' when the current locale is 'en-US'.
   */
  public get currentRegion(): Region {
    return this.getRegionOfCountry(this.currentLocale.country);
  }

  /**
   * @deprecated Use {@link EnvironmentService.siteID} instead.
   * **NOTE**: They are not equivalent in every case. Before updating some
   * usage, confirm it is the appropriate value.
   *
   * Gets the current Site ID.
   * @returns A string of the current brand hyphenated with the current locale.
   * @example
   * 'SANUK-US'
   * 'HOKA-UK'
   */
  public get siteID(): SiteID {
      const brand = ('AHNU');
    const { country } = this.currentLocale;
    return `${brand}-${country}` as SiteID; // casting due to bad types in deprecated code.
  }

  /**
   * Gets an object representing a {@link ILocale Locale} given a
   * {@link ConfiguredLocaleString locale string}.
   *
   * @param str - A string that either satisfies the {@link ConfiguredLocaleString} type,
   * or represents a {@link Country} or {@link Language}. In the latter two cases, the missing
   * component will take a default value.
   *
   * @returns An {@link ILocale} object.
   * @throws `InvalidArgumentError` if the passed in `str` is not a valid locale
   * string.
   */
  public getLocaleFromString(
    str: ConfiguredLocaleString | Language | Country
  ): ILocale {
    // Bad type casts can allow non-strings to come in. In those cases, throw an error
    // explaining what went wrong.
    if (typeof str !== 'string') {
      throw new InvalidArgumentError(
        `Value passed to parameter \`str\` must be type \`string\`, got value "${str}".`
      );
    }

    // Re-use the object if possible since we'll likely read the value more often than we'll rewrite it.
    const cachedIConfiguredLocaleString =
      this._ConfiguredLocaleStringRecord[str];
    if (cachedIConfiguredLocaleString) {
      return cachedIConfiguredLocaleString;
    }

    let [language, country] = str.split('-');
    const [defaultLang, defaultCountry] = ['en', 'US'];

    // If country is undefined, the string was only one part, rather than two.
    if (!country) {
      // If the `language` variable was indeed a language, fallback to the default country.
      if (Object.values(Language).includes(language as Language)) {
        country = defaultCountry;
      }
      // Otherwise, see if the `language` variable is a country instead.
      else if (Object.values(Country).includes(language as Country)) {
        language = defaultLang;
      }
      // Else, this string is bogus. Throw.
      else {
        throw new InvalidArgumentError(
          `\`getLocaleFromString\` was passed string "${str}" as a locale string, but this is not a valid locale string.`
        );
      }
    }

    // Create the ILocale result object.
    const result: ILocale = {
      language: language as Language,
      country: country as Country,

      /** @inheritdoc */
      toString() {
        // This is cast to `unknown` first because it is technically possible that
        // this is not actually a valid combination of language and country at
        // runtime. This scenario is very unlikely though, and if necessary
        // should use runtime checks instead.
        return `${language as Language}-${
          country as Country
        }` as unknown as ConfiguredLocaleString;
      }
    };

    // Add the result to the ILocale object cache.
    this._ConfiguredLocaleStringRecord[str] = result;

    return result;
  }

  /**
   * Gets the {@link Region} associated with a given {@link Country}.
   * @param country - The country to get the region for.
   * @returns The region associated with the provided country.
   * @throws {}
   * @example
   * getRegionOfCountry(Country.US); // 'NA'
   */
  public getRegionOfCountry(country: Country): Region {
    const region = Object.entries(regionCountriesMap).find(([, locales]) =>
      (locales as ReadonlyArray<Country>).includes(country)
    ) as [Region, ReadonlyArray<Country>] | undefined;

    if (!region) {
      throw new LikelyMisconfigurationError(
        `Could not find a region for country "${country}". This is likely an indication of misconfiguration.`
      );
    }

    return region[0];
  }

  /**
   * Retrieves a localized resource string for the current locale given a resource string
   * object containing lower-case locale keys with localized string values.
   * @param messageObj - The resource string object.
   * @param locale - The locale to use for retrieving the localized string.
   * @returns The localized resource string.
   */
  private getMsgFromResourceObject(
    messageObj: Resource,
    locale: ConfiguredLocaleString
  ): string {
    const lowerCaseLocale =
      locale.toLowerCase() as Lowercase<ConfiguredLocaleString>;
    const localizedString = messageObj[lowerCaseLocale];

    // If the locale is not found, fallback to the default locale.
    if (isNullOrEmpty(localizedString)) {
      return messageObj.default;
    }

    // If the localized string was found for the locale, return it.
    return localizedString;
  }

  /**
   * **Note:** This method should not be invoked directly in most development scenarios.
   * This is because it does not participate in the static optimizations that inline resource
   * strings. Instead, use the `msg` function defined in the adjacent `index.ts` file.
   *
   * Retrieves a localized resource string for the given identifier and locale (or fallbacks
   * to the "current" locale if none is provided). The resource string must be defined in the
   * `lang` directory.
   *
   * If the resource string contains placeholders, use {@link msgf} instead.
   *
   * @param msgID - The identifier for the resource string. It must be in the form of
   * `[file].[key].[...etc]`, where `file` is the name of the file in the `lang`,
   * `key` is the key in the exported object, and `...etc` is any sequence of nested keys
   * which resolve to a resource string.
   * @param [langLocale] - The lang-locale of the given message.
   * @returns The localized resource string.
   * @throws An error when in production and the argument is not an inline resource string
   * container object.
   */
  public msg<Path extends LocalizationStringPath>(
    msgID: Path | Resource,
    langLocale?: ILocale
  ): LocalizationStringMap[Path] {
    const locale = langLocale?.toString() ?? this.currentLocale.toString();

    // If the `msgID` is an object, then it is a resource string that has been inlined
    // by the static optimizations. In this case, we can just return the string directly
    // from the resource object of locale strings.
    if (msgID instanceof Object) {
      return this.getMsgFromResourceObject(
        msgID,
        locale
      ) as LocalizationStringMap[Path];
    }

    // If we're in production and we've come this far, it means that this method was
    // called, but not passed an inline resource string. This is a problem because
    // the static optimizations should have inlined all resource strings. We throw here
    // because if we allow it to go unchecked, we will suffer performance bloat from
    // always including all resource strings - something not acceptable for production.
    //
    // In storybook we load these strings dynamically. In the long run we will want to
    // run static generation and optimization for storybook as well.
    if (
      process.env.NODE_ENV === 'production' &&
      !(typeof __isolatedContext__ !== "undefined")
    ) {
      throw new Error(
        'For production builds, all resource strings should be inlined per our static optimizations. ' +
          `Something went wrong and the resource string for ID "${
            msgID
          }" was not inlined.`
      );
    }

    // Otherwise, we're in the development environment. In this case, we need to
    // dynamically load the resource strings for the current locale from the pre-built
    // objects that declare all of our resource strings. This method works best with hot-reloading.
    // Get the resource strings from the cache, or create a new one for this
    // locale.
    const resourceStrings = this.resourceStringCache.get(
      locale,
      () => {
        return new ResourceStrings(locale);
      },
      GetMethod.Sync
    );

    return resourceStrings.msg(msgID);
  }

  /**
   * **Note:** This method should not be invoked directly in most development scenarios.
   * This is because it does not participate in the static optimizations that inline resource
   * strings. Instead, use the `msgf` function defined in the adjacent `index.ts` file.
   *
   * Retrieves a configurable, localized resource string for the given identifier and locale
   * (or fallbacks to the "current" locale if none is provided). The resource string must be
   * defined in the `lang` directory.
   *
   * The resource string should contain placeholders in ICU MessageFormat syntax, which can be
   * interpolated with the values provided in the `values` parameter. If the resource string
   * does not contain placeholders, use {@link msg} instead.
   *
   * @param msgID - The resource string identifier for this resource. Will be a dot
   * notation string in the form of `file.key.nestedKey.etc` where `file` is the name of
   * the file in the `lang` directory, `key` is the key in the exported object, and
   * `nestedKey` could be a nested key in the object, and so on.
   * @param values - A record of values to be interpolated into the string. The values
   * provided should match the placeholders in the resource string.
   * @param [locale] - The lang-locale for the given message.
   * @returns The localized resource string formatted with the passed in values in
   * accordance with the Intl MessageFormat spec.
   * @throws An error when in production and the argument is not an inline resource string
   * container object.
   *
   * @see {@link https://formatjs.github.io/docs/core-concepts/icu-syntax/ ICU Message Syntax}
   * @see {@link https://formatjs.github.io/docs/intl-messageformat/#common-usage-example Common Usage Example}
   */
  public msgf<Path extends LocalizationStringPath>(
    msgID: Path,
    values: Record<
      string,
      Nullable<Primitive | ReactElement<unknown> | JSX.Element>
    >,
    locale?: ILocale
  ): LocalizationStringMap[Path] {
    const unformattedMsg = this.msg(msgID, locale);

    return new IntlMessageFormat(
      unformattedMsg,
      locale?.toString() ?? this.currentLocale.toString()
    ).format(
      values as Record<
        string,
        | string
        | number
        | boolean
        | null
        | undefined
        | ReactElement<unknown>
        | JSX.Element
      >
    ) as LocalizationStringMap[Path];
  }

  /**
   * @deprecated Use `msg` instead for better performance and type safety.
   *
   * Dynamically retrieves a localized resource string for the given identifier in the
   * "current" locale. The resource string must be defined in the `lang` directory.
   * The difference between this method and {@link msg} is that this method will return
   * a {@link StaleWhileRevalidate} object that can be used to asynchronously load and display
   * the resource string.
   *
   * **Note:** This method is _not_ type safe. It is recommended to use `msg`
   * instead for type safety. Furthermore, this method should only be used when the
   * resource ID is not known at compile time. The method necessarily requires that all
   * resource strings be loaded at runtime, which can have a sizeable impact on the
   * perceived performance of the application.
   *
   * **Note 2:** There is no dynamic version of `msgf` because if the resource ID is not
   * known at compile time, then it's unlikely that the values to be interpolated are known
   * either.
   *
   * @param msgID - The identifier for the resource string. It must be in the form of
   * `[file].[key].[...etc]`, where `file` is the name of the file in the `lang`,
   * `key` is the key in the exported object, and `...etc` is any sequence of nested keys
   * which resolve to a resource string.
   * @returns The localized resource string.
   */
  public msgDynamic(msgID: string): StaleWhileRevalidate<string> {
    const locale = this.currentLocale.toString();

    // Get the resource strings from the cache, or create a new one for this
    // locale.
    const resourceStrings = this.resourceStringCache.get(
      locale,
      () => {
        return new ResourceStrings(locale);
      },
      GetMethod.Sync
    );

    return resourceStrings.msgDynamic(msgID);
  }

  /**
   * Formats a number as currency based on the current locale
   * and options provided.
   * @param amount - The amount to format.
   * @param options - Options to pass to the formatter.
   * @returns The current currency code.
   */
  public formatCurrency(
    amount: number,
    options?: Intl.NumberFormatOptions
  ): string {
    const defaultCurrency = options?.currency ?? ('USD' as Currency);
    return new Intl.NumberFormat(this.currentLocale.toString(), {
      style: 'currency',
      currency: defaultCurrency,
      ...options
    }).format(amount);
  }

  /**
   * Formats a number as currency based on the current locale
   * and options provided, but without the currency symbol.
   * @param amount - The amount to format.
   * @param options - Options to pass to the formatter.
   * @returns The current currency code.
   * @example
   * I18NService.formatCurrencyNoSymbol(0); // '0.00' (if the current currency is USD)
   * I18NService.formatCurrencyNoSymbol(
   *  1234.56,
   *  { currency: 'JPY' }
   * ); // '1,235'
   */
  public formatCurrencyNoSymbol(
    amount: number,
    options?: Omit<Intl.NumberFormatOptions, 'style'>
  ): string {
    const defaultCurrency = options?.currency ?? this.currency;
    const currentLocale = this.currentLocale.toString();

    const { minimumFractionDigits, maximumFractionDigits } =
      new Intl.NumberFormat(currentLocale, {
        style: 'currency',
        currency: defaultCurrency,
        ...options
      }).resolvedOptions();

    return amount.toLocaleString(currentLocale, {
      minimumFractionDigits,
      maximumFractionDigits
    });
  }

  /**
   * Takes a date and formats it based on the current locale and options provided.
   * @param date - The date to format.
   * @param options - Options to pass to the formatter.
   * @returns The date formatted.
   */
  public formatDate(date: Date, options?: Intl.DateTimeFormatOptions): string {
    return new Intl.DateTimeFormat(
      this.currentLocale.toString(),
      options
    ).format(date);
  }

  /**
   * Retrieves the currency symbol based in current locale.
   * @returns - The current currency symbol.
   */
  public currentCurrencySymbol(): string {
    return new Intl.NumberFormat(this.currentLocale.toString(), {
      style: 'currency',
      currency: this.currency
    })
      .formatToParts(1)
      .find((x) => x.type === 'currency')?.value as string;
  }

  /**
   * Given an arrival date or date range, returns a pretty formatted string for
   * display purposes. For each date, the format will be determined by the rules
   * of the specified language and locale. A hyphen will separate both dates
   * if two dates are provided.
   *
   * @param latestArrival - The latest date by which the shipment should be
   * delivered. If an `earliestArrival` date is provided, this will be the upper
   * bound in the delivery date range.
   *
   * @param earliestArrival - The earliest date at which the shipment could be
   * delivered. This value will be the lower bound in the delivery date range.
   *
   * @param locale - The {@link ILocale locale} to format for. Defaults to the
   * app's current language and country.
   *
   * @returns The formatted string.
   *
   * @example `Monday, 4/11`, `Monday, 4/11 - Wednesday, 4/14`
   */
  public formatDeliveryDate(
    latestArrival: Date,
    earliestArrival?: Nullable<Date>,
    locale: ConfiguredLocaleString = this.currentLocale.toString()
  ): string {
    const formattedLatestArrival = new Intl.DateTimeFormat(locale, {
      weekday: 'long',
      month: 'numeric',
      day: 'numeric'
    }).format(latestArrival);

    const formattedEarliestArrival = earliestArrival
      ? new Intl.DateTimeFormat(locale, {
          weekday: 'long',
          month: 'numeric',
          day: 'numeric'
        }).format(earliestArrival)
      : null;

    return !isNullOrEmpty(formattedEarliestArrival)
      ? `${formattedEarliestArrival} - ${formattedLatestArrival}`
      : formattedLatestArrival;
  }
}

export default new I18NService();
